import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, UserCog, History, Search, FilePlus2, Files, Smartphone } from "lucide-react";
import PortalDialog from "@/components/PortalDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge, EmptyState, Field, Spinner, Money } from "@/components/common";
import { api, formatErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const BLANK = { company_name: "", vat_number: "", sponsor_name: "", contact_person: "", email: "", mobile: "", address: "", reference_by: "", vat_filing_frequency: "monthly", status: "active", primary_staff_id: "" };
const FIELDS = [["company_name", "Company name", true, 2], ["vat_number", "VAT registration number", true], ["sponsor_name", "Sponsor / customer name"], ["contact_person", "Contact person"], ["mobile", "Mobile"], ["email", "Email"], ["reference_by", "Reference by (optional)"], ["address", "Address", false, 2]];

const StaffSelect = ({ value, onChange, staff, testid }) => (
  <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Select staff" /></SelectTrigger>
    <SelectContent className="bg-white max-h-72">
      <SelectItem value="none">— Not assigned —</SelectItem>
      {staff.filter((s) => s.status === "active").map((s) => <SelectItem key={s.id} value={s.id}>{s.name} <span className="text-slate-400 num text-xs">{s.employee_code}</span></SelectItem>)}
    </SelectContent>
  </Select>
);

const ClientDialog = ({ open, onOpenChange, initial, staff, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...BLANK, ...initial, primary_staff_id: initial.primary_staff_id || "" } : BLANK); }, [open, initial]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, primary_staff_id: f.primary_staff_id || null };
      initial ? await api.put(`/clients/${initial.id}`, body) : await api.post("/clients", body);
      toast.success(initial ? "Client updated" : "Client created");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{initial ? `Edit ${initial.code}` : "New client"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map(([k, l, req, span]) => (
            <div key={k} className={span === 2 ? "col-span-2" : ""}>
              <Field label={l} required={req} hint={k === "vat_number" ? "15 digits, starts and ends with 3" : undefined}>
                <Input value={f[k]} maxLength={k === "vat_number" ? 15 : undefined} className={k === "vat_number" ? "num" : ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`client-form-${k}`} />
              </Field>
            </div>
          ))}
          <Field label="Primary in-charge staff"><StaffSelect value={f.primary_staff_id} onChange={(v) => setF({ ...f, primary_staff_id: v })} staff={staff} testid="client-form-staff" /></Field>
          <Field label="VAT return filing" hint="Drives the default period in VAT Consolidation">
            <Select value={f.vat_filing_frequency || "monthly"} onValueChange={(v) => setF({ ...f, vat_filing_frequency: v })}>
              <SelectTrigger data-testid="client-form-filing"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="monthly">Monthly filer</SelectItem><SelectItem value="quarterly">Quarterly filer</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="client-form-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.company_name || !f.vat_number} className="bg-emerald-800 hover:bg-emerald-900" data-testid="client-form-submit">{busy ? "Saving…" : "Save client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssignDialog = ({ client, staff, onOpenChange, onSaved }) => {
  const [sid, setSid] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { setSid(client?.primary_staff_id || ""); setNote(""); }, [client]);
  const save = async () => {
    try { await api.put(`/clients/${client.id}/assign`, { staff_id: sid, note }); toast.success("Assignment updated"); onSaved(); onOpenChange(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign in-charge staff</DialogTitle><DialogDescription>{client?.company_name}. Historical entries stay attributed to their original creator.</DialogDescription></DialogHeader>
        <StaffSelect value={sid} onChange={setSid} staff={staff} testid="assign-staff-select" />
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} data-testid="assign-note-input" />
        <DialogFooter><Button disabled={!sid} onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="assign-submit-button">Save assignment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HistoryDialog = ({ client, onOpenChange }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (client) api.get(`/clients/${client.id}/assignments`).then(({ data }) => setRows(data)); }, [client]);
  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" data-testid="assignment-history-dialog">
        <DialogHeader><DialogTitle>Assignment history · {client?.company_name}</DialogTitle></DialogHeader>
        {!rows ? <Spinner /> : !rows.length ? <EmptyState title="No assignments yet" /> : (
          <table className="w-full"><thead><tr>{["Effective", "Previous", "New staff", "By", "Ended"].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id}><td className="td num text-xs">{fmtDateTime(r.start_date)}</td><td className="td text-slate-500">{r.previous_staff_name || "—"}</td><td className="td font-medium">{r.staff_name}</td><td className="td text-xs">{r.assigned_by_name}</td><td className="td num text-xs">{r.end_date ? fmtDateTime(r.end_date) : <StatusBadge status="active" />}</td></tr>)}</tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Clients() {
  const { user } = useAuth();
  const admin = user.role === "super_admin";
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [unassigned, setUnassigned] = useState(sp.get("unassigned") === "1");
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const [hist, setHist] = useState(null);
  const [portal, setPortal] = useState(null);

  const load = () => api.get("/clients").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    if (admin) api.get("/users", { params: { role: "staff" } }).then(({ data }) => setStaff(data));
  }, [admin]);

  const shown = (rows || []).filter((c) => (!unassigned || !c.primary_staff_id) && (!q || `${c.company_name} ${c.vat_number} ${c.code} ${c.sponsor_name}`.toLowerCase().includes(q.toLowerCase())));
  const title = admin ? "Clients & assignments" : user.role === "staff" ? "My assigned clients" : "Authorised clients";

  return (
    <div>
      <PageHeader eyebrow={admin ? "Management" : "Clients"} title={title} subtitle={admin ? "Maintain client VAT accounts and assign a primary in-charge staff member." : undefined}
        actions={admin && <Button onClick={() => setEdit({})} className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-client-button"><Plus className="w-4 h-4 mr-1.5" />New client</Button>} />
      <div className="panel overflow-hidden fade-up">
        <div className="flex flex-wrap items-center gap-4 px-4 py-3 border-b border-slate-200">
          <div className="relative flex-1 min-w-[220px] max-w-md"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, VAT no., code…" className="pl-9 h-9" data-testid="client-search-input" /></div>
          {admin && <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={unassigned} onCheckedChange={setUnassigned} data-testid="client-filter-unassigned" />Unassigned only</label>}
          <span className="ml-auto num text-xs text-slate-500">{shown.length} clients</span>
        </div>
        <div className="overflow-x-auto">
          {!rows ? <Spinner /> : !shown.length ? <EmptyState title="No clients" /> : (
            <table className="w-full" data-testid="clients-table">
              <thead><tr>{["Code", "Company", "VAT no.", "VAT filing", "Sponsor", "In-charge staff", "Bills", "VAT recorded", "Status", ""].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody>
                {shown.map((c, i) => (
                  <tr key={c.id} className="row-hover" data-testid={`client-row-${i}`}>
                    <td className="td num text-xs text-slate-500">{c.code}</td>
                    <td className="td"><div className="font-medium text-slate-800">{c.company_name}</div><div className="text-[11px] text-slate-400">{c.contact_person} {c.mobile}{c.reference_by ? ` · Ref: ${c.reference_by}` : ""}</div></td>
                    <td className="td num text-xs">{c.vat_number}</td>
                    <td className="td"><span className="text-[11px] font-medium px-2 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700" data-testid={`client-filing-${i}`}>{c.vat_filing_frequency === "quarterly" ? "Quarterly" : "Monthly"}</span></td>
                    <td className="td text-sm text-slate-600">{c.sponsor_name || "—"}</td>
                    <td className="td">{c.primary_staff_name ? <span className="text-sm">{c.primary_staff_name}</span> : <span className="text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">Unassigned</span>}</td>
                    <td className="td num text-sm">{c.bill_count}</td>
                    <td className="td"><Money value={c.vat_total} /></td>
                    <td className="td"><StatusBadge status={c.status} /></td>
                    <td className="td text-right whitespace-nowrap">
                      {admin ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setEdit(c)} data-testid={`client-edit-${i}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setAssign(c)} data-testid={`client-assign-${i}`} title="Assign staff"><UserCog className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setHist(c)} data-testid={`client-history-${i}`} title="Assignment history"><History className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setPortal(c)} data-testid={`client-portal-${i}`} title="Client portal URL & PIN"><Smartphone className="w-4 h-4 text-emerald-700" /></Button>
                        </>
                      ) : user.role === "staff" ? (
                        <Button size="sm" variant="outline" onClick={() => nav(`/bills/new?client=${c.id}`)} data-testid={`client-add-bill-${i}`}><FilePlus2 className="w-3.5 h-3.5 mr-1" />Add purchase</Button>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => nav(`/bills?client_id=${c.id}`)} data-testid={`client-bills-${i}`} title="View bills"><Files className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {admin && (
        <>
          <ClientDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit?.id ? edit : null} staff={staff} onSaved={load} />
          <AssignDialog client={assign} staff={staff} onOpenChange={() => setAssign(null)} onSaved={load} />
          <HistoryDialog client={hist} onOpenChange={() => setHist(null)} />
          <PortalDialog client={portal} onOpenChange={() => setPortal(null)} />
        </>
      )}
    </div>
  );
}
